/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  MenuItem,
  Typography,
  CircularProgress,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Paper,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import { pdf } from "@react-pdf/renderer";
import PayslipPDF from "./PayslipPDF";
import { fetchMonthlyPayrollReport } from "store/features/company/payrollManagement/processPayroll/processPayrollActions";
import {
  selectPayrollLoading,
  selectPayrollTransactions,
} from "store/features/company/payrollManagement/processPayroll/processPayrollSelectors";
import downloadPayslipSvg from "assets/modals/download.svg";

const DownloadPayslipModal = ({
  isOpen,
  onClose,
  month: currentMonth,
  year: currentYear,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const loading = useSelector(selectPayrollLoading);
  const transactions = useSelector(selectPayrollTransactions);

  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [employeeId, setEmployeeId] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - 2 + i,
  );

  // Fetch employees when month/year changes or modal opens
  useEffect(() => {
    if (isOpen && month && year) {
      fetchPayrollData();
    }
  }, [isOpen, month, year]);

  const fetchPayrollData = async () => {
    setLocalLoading(true);
    setFetchError(null);
    setEmployeeId("");
    setSelectedEmployee(null);

    try {
      const result = await dispatch(fetchMonthlyPayrollReport({ month, year }));
      if (!result?.success) {
        setFetchError(result?.error || "Failed to fetch payroll data");
      }
    } catch (err) {
      console.error("Error fetching payroll:", err);
      setFetchError("Failed to fetch employees. Please try again.");
    } finally {
      setLocalLoading(false);
    }
  };

  const handleEmployeeChange = (event) => {
    const empId = event.target.value;
    setEmployeeId(empId);
    const employee = transactions.find((emp) => emp.id === parseInt(empId));
    setSelectedEmployee(employee);
  };

  const canDownload = () => {
    if (!selectedEmployee) return false;
    const allowedStatuses = ["processed", "approved", "paid"];
    return allowedStatuses.includes(selectedEmployee.status?.toLowerCase());
  };

  const getStatusMessage = () => {
    if (!selectedEmployee) return "";
    if (!canDownload()) {
      return `This employee has a "${selectedEmployee.status}" transaction. Payslip can only be downloaded for employees with status: Processed, Approved, or Paid.`;
    }
    return "";
  };

  const handleDownload = async () => {
    if (!selectedEmployee || !canDownload()) return;

    setDownloading(true);
    try {
      // Create PDF blob
      const blob = await pdf(
        <PayslipPDF data={selectedEmployee} month={month} year={year} />,
      ).toBlob();

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `payslip_${selectedEmployee.employee_code}_${
        months[month - 1]
      }_${year}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Close modal after successful download
      setTimeout(() => {
        onClose();
        resetForm();
      }, 1500);
    } catch (err) {
      console.error("Error generating PDF:", err);
      setFetchError("Failed to generate payslip. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const resetForm = () => {
    setMonth(currentMonth);
    setYear(currentYear);
    setEmployeeId("");
    setSelectedEmployee(null);
    setFetchError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return {
          bg: "#dbeafe",
          color: "#1e40af",
          label: "Approved",
          icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
        };
      case "paid":
        return {
          bg: "#d1fae5",
          color: "#065f46",
          label: "Paid",
          icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
        };
      case "processed":
        return {
          bg: "#e0e7ff",
          color: "#3730a3",
          label: "Processed",
          icon: <ReceiptIcon sx={{ fontSize: 14 }} />,
        };
      case "failed":
        return {
          bg: "#fee2e2",
          color: "#991b1b",
          label: "Failed",
          icon: <CancelIcon sx={{ fontSize: 14 }} />,
        };
      default:
        return {
          bg: "#fef3c7",
          color: "#92400e",
          label: status || "Pending",
          icon: <CancelIcon sx={{ fontSize: 14 }} />,
        };
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(amount));
  };

  const isLoading = localLoading || loading;

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#f59e0b",
    },
  };

  const getMonthName = (m) => {
    return months[m - 1];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <style>
            {`
              .MuiPopover-root { z-index: 1400 !important; }
              .MuiPopper-root { z-index: 1400 !important; }
              .MuiMenu-root { z-index: 1400 !important; }
            `}
          </style>

          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          {/* Slide-in Panel */}
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "600px",
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={downloadPayslipSvg}
                      alt="Download Payslip"
                      sx={{ width: 80, height: 80, objectFit: "contain" }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color:
                          theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                      }}
                    >
                      Download Payslip
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color:
                          theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      Download employee payslip for a specific month
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <IconButton
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#f59e0b", color: "#ffffff" },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3 }}>
              <Stack spacing={3}>
                {/* Payroll Period Selection */}
                <Accordion
                  defaultExpanded
                  sx={{
                    borderRadius: "16px !important",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                    boxShadow: "none",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        alignItems: "center",
                        gap: 1,
                      },
                    }}
                  >
                    <PdfIcon sx={{ color: "#f59e0b" }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Select Pay Period
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                      >
                        <TextField
                          fullWidth
                          select
                          label="Month"
                          value={month}
                          onChange={(e) => {
                            setMonth(parseInt(e.target.value));
                            setEmployeeId("");
                            setSelectedEmployee(null);
                          }}
                          onBlur={() => fetchPayrollData()}
                          required
                          sx={inputSx}
                          InputLabelProps={{ shrink: true }}
                        >
                          {months.map((m, idx) => (
                            <MenuItem key={idx} value={idx + 1}>
                              {m}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          fullWidth
                          select
                          label="Year"
                          value={year}
                          onChange={(e) => {
                            setYear(parseInt(e.target.value));
                            setEmployeeId("");
                            setSelectedEmployee(null);
                          }}
                          onBlur={() => fetchPayrollData()}
                          required
                          sx={inputSx}
                          InputLabelProps={{ shrink: true }}
                        >
                          {years.map((y) => (
                            <MenuItem key={y} value={y}>
                              {y}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                {/* Employee Selection */}
                <Accordion
                  defaultExpanded
                  sx={{
                    borderRadius: "16px !important",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                    boxShadow: "none",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        alignItems: "center",
                        gap: 1,
                      },
                    }}
                  >
                    <PersonIcon sx={{ color: "#f59e0b" }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Select Employee
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <TextField
                        select
                        label="Choose Employee"
                        fullWidth
                        value={employeeId}
                        onChange={handleEmployeeChange}
                        disabled={isLoading || transactions.length === 0}
                        required
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      >
                        <MenuItem value="">Select an employee...</MenuItem>
                        {isLoading ? (
                          <MenuItem disabled>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <CircularProgress size={16} />
                              <Typography variant="body2">
                                Loading employees...
                              </Typography>
                            </Stack>
                          </MenuItem>
                        ) : transactions.length === 0 ? (
                          <MenuItem disabled>
                            No employees found for {getMonthName(month)} {year}
                          </MenuItem>
                        ) : (
                          transactions.map((emp) => (
                            <MenuItem key={emp.id} value={emp.id}>
                              {emp.employee_name} ({emp.employee_code}) -{" "}
                              {emp.department_name}
                            </MenuItem>
                          ))
                        )}
                      </TextField>

                      {/* Error Message */}
                      {fetchError && (
                        <Alert severity="error" sx={{ borderRadius: "12px" }}>
                          {fetchError}
                        </Alert>
                      )}

                      {/* No Data Message */}
                      {!isLoading &&
                        transactions.length === 0 &&
                        !fetchError && (
                          <Alert severity="info" sx={{ borderRadius: "12px" }}>
                            No payroll transactions found for{" "}
                            {getMonthName(month)} {year}. Please process payroll
                            first.
                          </Alert>
                        )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                {/* Employee Details & Status */}
                {selectedEmployee && (
                  <Accordion
                    defaultExpanded
                    sx={{
                      borderRadius: "16px !important",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <ReceiptIcon sx={{ color: "#f59e0b" }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Payslip Summary
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        {/* Employee Info Card */}
                        <Paper
                          sx={{
                            p: 2,
                            borderRadius: "16px",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(30,41,59,0.6)"
                                : "#fefce8",
                            border: `1px solid ${
                              theme.palette.mode === "dark"
                                ? "#334155"
                                : "#fef3c7"
                            }`,
                          }}
                        >
                          <Stack spacing={2}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ mb: 1 }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#f59e0b",
                                }}
                              >
                                Employee Information
                              </Typography>

                              <Chip
                                icon={
                                  getStatusColor(selectedEmployee.status).icon
                                }
                                label={getStatusColor(
                                  selectedEmployee.status,
                                ).label.toUpperCase()}
                                size="small"
                                sx={{
                                  backgroundColor: getStatusColor(
                                    selectedEmployee.status,
                                  ).bg,
                                  color: getStatusColor(selectedEmployee.status)
                                    .color,
                                  fontWeight: 600,
                                  fontFamily: "monospace",
                                  letterSpacing: "0.5px",
                                  ml: "auto",

                                  "& .MuiChip-label": {
                                    px: 1,
                                  },
                                }}
                              />
                            </Stack>
                            <Divider
                              sx={{
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "#334155"
                                    : "#fef3c7",
                              }}
                            />
                            <Stack spacing={1.5}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ gap: 3 }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#8790a1" }}
                                >
                                  Employee Name:
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {selectedEmployee.employee_name}
                                </Typography>
                              </Stack>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ gap: 3 }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#8790a1" }}
                                >
                                  Employee Code:
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {selectedEmployee.employee_code}
                                </Typography>
                              </Stack>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ gap: 3 }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#8790a1" }}
                                >
                                  Department:
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {selectedEmployee.department_name}
                                </Typography>
                              </Stack>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ gap: 3 }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#8790a1" }}
                                >
                                  Payment Date:
                                </Typography>
                                <Typography variant="body2">
                                  {selectedEmployee.payment_date
                                    ? new Date(
                                        selectedEmployee.payment_date,
                                      ).toLocaleDateString()
                                    : "-"}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Stack>
                        </Paper>

                        {/* Salary Summary Card */}
                        <Paper
                          sx={{
                            p: 2,
                            borderRadius: "16px",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(30,41,59,0.6)"
                                : "#fefce8",
                            border: `1px solid ${
                              theme.palette.mode === "dark"
                                ? "#334155"
                                : "#fef3c7"
                            }`,
                          }}
                        >
                          <Stack spacing={1.5}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, color: "#f59e0b" }}
                            >
                              Salary Summary
                            </Typography>
                            <Divider
                              sx={{
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "#334155"
                                    : "#fef3c7",
                              }}
                            />
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              sx={{ gap: 3 }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: "#8790a1" }}
                              >
                                Basic Salary:
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {formatCurrency(selectedEmployee.basic_salary)}
                              </Typography>
                            </Stack>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              sx={{ gap: 3 }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: "#8790a1" }}
                              >
                                Total Earnings:
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="#10b981"
                              >
                                {formatCurrency(
                                  selectedEmployee.total_earnings,
                                )}
                              </Typography>
                            </Stack>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              sx={{ gap: 3 }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: "#8790a1" }}
                              >
                                Total Deductions:
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="#ef4444"
                              >
                                {formatCurrency(
                                  selectedEmployee.total_deductions,
                                )}
                              </Typography>
                            </Stack>
                            <Divider
                              sx={{
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "#334155"
                                    : "#fef3c7",
                              }}
                            />
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              sx={{ gap: 3 }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: "#f59e0b" }}
                              >
                                Net Salary:
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: 800, color: "#f59e0b" }}
                              >
                                {formatCurrency(selectedEmployee.net_salary)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Paper>

                        {/* Status Message */}
                        {!canDownload() && (
                          <Alert
                            severity="warning"
                            sx={{ borderRadius: "12px" }}
                          >
                            {getStatusMessage()}
                          </Alert>
                        )}

                        {canDownload() && (
                          <Alert
                            severity="success"
                            sx={{ borderRadius: "12px" }}
                          >
                            This employee's payslip is ready for download.
                          </Alert>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Stack>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 3,
                borderTop: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                display: "flex",
                gap: 2,
                justifyContent: "space-between",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                zIndex: 10,
              }}
            >
              <Button
                onClick={handleClose}
                disabled={downloading}
                variant="contained"
                sx={{
                  borderRadius: "12px",
                  px: 4,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 500,
                  minWidth: "150px",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#374151",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDownload}
                disabled={!canDownload() || downloading}
                variant="contained"
                startIcon={
                  downloading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                sx={{
                  borderRadius: "12px",
                  px: 4,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "250px",
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e5e7eb",
                    color: "#9ca3af",
                  },
                }}
              >
                {downloading
                  ? "Generating PDF..."
                  : `Download Payslip for ${getMonthName(month)} ${year}`}
              </Button>
            </Box>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DownloadPayslipModal;
